import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  ScrollText,
  HeartHandshake,
  ClipboardCheck,
  FileCheck2,
  Send,
  BadgeCheck,
  Plus,
  Minus,
} from "lucide-react";
import SEO from "../components/SEO";
import { serviceGroups, groupPath } from "../data/services";
import { legal, has } from "../data/legal";
import faqs from "../data/faqData.json";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function Hero() {
  return (
    <section className="relative overflow-hidden bg-acBg">
      <div className="container-page grid items-center gap-12 py-16 lg:grid-cols-2 lg:py-24">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <p className="eyebrow">Migration support and coordination</p>
          <h1 className="mt-4 text-4xl font-bold leading-tight text-acblue sm:text-5xl">
            The right people, from your first document to your first day.
          </h1>
          <p className="mt-5 max-w-xl text-lg text-acBlack/70">
            We build your file to a lodgement standard, evidence organised and
            gaps closed, then put you with a registered migration agent we have
            vetted and stay on the matter until it is done. Applications are won
            or lost on the paperwork, and that is our craft.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/eligibility-quiz/" className="btn-primary">
              Check your eligibility
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/book-consultation/" className="btn-ghost">
              Book a consultation
            </Link>
          </div>
          <p className="mt-6 flex items-center gap-2 text-sm text-acBlack/60">
            <ShieldCheck className="h-4 w-4 text-acAccent" />
            We match you with a registered migration agent, bound by the Code of
            Conduct and professionally insured, for the regulated advice and
            lodgement.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="relative"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="card relative aspect-[4/5] overflow-hidden p-0">
              <img
                src={`${process.env.PUBLIC_URL}/hero-left.png`}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-acblue/45 via-acblue/15 to-acblue/10" />
            </div>
            <div className="card relative mt-10 aspect-[4/5] overflow-hidden p-0">
              <img
                src={`${process.env.PUBLIC_URL}/hero-right.png`}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-acblue/45 via-acblue/15 to-acblue/10" />
            </div>
          </div>
          <div className="card absolute -bottom-6 left-1/2 w-64 -translate-x-1/2 border-t-4 border-acGold text-center">
            <p className="text-3xl font-bold text-acblue">On your side</p>
            <p className="text-sm text-acBlack/60">
              your people on the ground, from your first document to your first
              day
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBar() {
  // Real signals only (no invented stats, D-004). These are statements of how
  // we work, not unverifiable numbers. Swap for real metrics when supplied.
  const items = [
    {
      icon: ShieldCheck,
      label: "Registered agents",
      sub: "bound by the Code of Conduct",
    },
    { icon: ScrollText, label: "Transparent fees", sub: "in writing, upfront" },
    { icon: BadgeCheck, label: "Honest timelines", sub: "no false promises" },
    {
      icon: HeartHandshake,
      label: "On your side",
      sub: "your people on the ground",
    },
  ];
  return (
    <section className="border-y border-acblue/10 bg-white">
      <div className="container-page grid grid-cols-2 gap-6 py-8 md:grid-cols-4">
        {items.map((it) => (
          <div key={it.label} className="flex items-center gap-3">
            <it.icon className="h-8 w-8 shrink-0 text-acAccent" />
            <div>
              <p className="text-sm font-semibold text-acblue">{it.label}</p>
              <p className="text-xs text-acBlack/60">{it.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ServiceGrid() {
  return (
    <section className="bg-acBg py-20">
      <div className="container-page">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">What we help with</p>
          <h2 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
            A pathway for every situation
          </h2>
          <p className="mt-4 text-acBlack/70">
            We prepare and coordinate your application across the full range of
            Australian visa pathways, and match you with a registered migration
            agent for the regulated advice. Not sure where to start? Tell us
            about your situation and we will point you the right way.
          </p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {serviceGroups.map((g) => (
            <Link
              key={g.slug}
              to={groupPath(g)}
              className="card group flex flex-col transition hover:-translate-y-1 hover:shadow-md"
            >
              <h3 className="text-lg font-semibold text-acblue">{g.title}</h3>
              <p className="mt-2 flex-1 text-sm text-acBlack/70">{g.blurb}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-acAccent">
                Explore
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const pillars = [
    {
      icon: FileCheck2,
      title: "Preparation done properly",
      body: "We build a clean, complete, submission-ready file. Documents gathered and checked, evidence organised, gaps closed. Applications are won or lost on the paperwork, and that is our craft.",
    },
    {
      icon: ShieldCheck,
      title: "A registered agent, kept on track",
      body: "The regulated advice and lodgement are done by a registered migration agent we have vetted, bound by the Code of Conduct and professionally insured. We are your single point of contact and keep the matter moving so no one goes quiet on you.",
    },
    {
      icon: HeartHandshake,
      title: "Your people on the ground",
      body: "You are trusting a process from the other side of the world. We are the ones you can see, here in Australia, on your side, from your first document to your first day.",
    },
  ];
  return (
    <section className="py-20">
      <div className="container-page grid gap-12 lg:grid-cols-3">
        <div>
          <p className="eyebrow">Why work with us</p>
          <h2 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
            We remove the risk.
          </h2>
          <p className="mt-4 text-acBlack/70">
            In an industry where the biggest risk is who you trust, we get you
            application-ready, put you with a registered, accountable agent, and
            stay beside you the whole way.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:col-span-2">
          {pillars.map((p) => (
            <div key={p.title} className="card">
              <p.icon className="h-9 w-9 text-acAccent" />
              <h3 className="mt-4 text-lg font-semibold text-acblue">
                {p.title}
              </h3>
              <p className="mt-2 text-sm text-acBlack/70">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: ClipboardCheck,
      title: "Tell us your situation",
      body: "Share where you are and where you want to get to. We work out how we can help and get you started.",
    },
    {
      icon: FileCheck2,
      title: "We prepare your file",
      body: "We gather and check your documents and build a clean, complete file, ready to a lodgement standard.",
    },
    {
      icon: ShieldCheck,
      title: "A registered agent advises and lodges",
      body: "We match you with a registered migration agent we have vetted. They give the migration advice and lodge your application.",
    },
    {
      icon: Send,
      title: "We keep it moving",
      body: "We stay your single point of contact, hold the matter to its milestones, and chase things down so nothing stalls.",
    },
  ];
  return (
    <section className="bg-acNavyDeep py-20 text-white">
      <div className="container-page">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-acGold">
            How it works
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            We prepare and coordinate. A registered agent advises and lodges.
          </h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10"
            >
              <div className="flex items-center justify-between">
                <s.icon className="h-9 w-9 text-acGold" />
                <span className="text-3xl font-bold text-white/20">
                  {i + 1}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-white/70">{s.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-white/60">
          Visa decisions are made solely by the Department of Home Affairs. No
          outcome is guaranteed.
        </p>
      </div>
    </section>
  );
}

function QuizTeaser() {
  return (
    <section className="py-20">
      <div className="container-page">
        <div className="card flex flex-col items-center gap-6 bg-acNavySoft/60 px-8 py-12 text-center md:flex-row md:text-left">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-acblue sm:text-3xl">
              Not sure where to start?
            </h2>
            <p className="mt-3 text-acBlack/70">
              Answer a few quick questions so we can see how we can help and get
              you to the right registered migration agent. It is free, takes
              about two minutes, and there is no obligation.
            </p>
          </div>
          <Link to="/eligibility-quiz/" className="btn-primary shrink-0">
            Start the eligibility check
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-acblue/10 py-4">
      <button
        className="flex w-full items-center justify-between gap-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="font-semibold text-acblue">{q}</span>
        {open ? (
          <Minus className="h-5 w-5 shrink-0 text-acAccent" />
        ) : (
          <Plus className="h-5 w-5 shrink-0 text-acAccent" />
        )}
      </button>
      {open && <p className="mt-3 text-sm text-acBlack/70">{a}</p>}
    </div>
  );
}

function Faqs() {
  return (
    <section className="bg-acBg py-20">
      <div className="container-page max-w-3xl">
        <div className="text-center">
          <p className="eyebrow">Common questions</p>
          <h2 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
            Straight answers
          </h2>
        </div>
        <div className="mt-10">
          {faqs.map((f) => (
            <FaqItem key={f.q} {...f} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Consultation() {
  return (
    <section className="py-20">
      <div className="container-page grid gap-10 lg:grid-cols-2">
        <div>
          <p className="eyebrow">Get started</p>
          <h2 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
            Book a consultation
          </h2>
          <p className="mt-4 text-acBlack/70">
            Talk through your situation with our team. We will explain how we
            prepare your file and coordinate your matter, and match you with a
            registered migration agent for the regulated advice and lodgement.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-acBlack/80">
            {has(legal.phone) && <li>Call us: {legal.phone}</li>}
            {has(legal.email) && <li>Email: {legal.email}</li>}
            <li>Hours: {legal.hours}</li>
          </ul>
        </div>
        <div className="card">
          <h3 className="text-lg font-semibold text-acblue">
            Request a callback
          </h3>
          {/* Static demo form for the homepage preview. Wired to the backend /
              Netlify Forms in WP4 (DECISIONS D-005). */}
          <form className="mt-4 space-y-4" onSubmit={(e) => e.preventDefault()}>
            <input
              className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
              placeholder="Your name"
            />
            <input
              className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
              placeholder="Email"
              type="email"
            />
            <textarea
              className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
              rows={3}
              placeholder="Tell us about your situation"
            />
            <button type="submit" className="btn-primary w-full">
              Request callback
            </button>
            <p className="text-xs text-acBlack/50">
              By contacting us you agree to our privacy policy. No outcome is
              guaranteed; visa decisions are made by the Department of Home
              Affairs.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title=""
        description="Migration support and coordination. We get your application ready, match you with a registered migration agent for the regulated advice and lodgement, and stay on the matter until it is done. Transparent fees, no outcome guaranteed."
        path="/"
      />
      <Hero />
      <TrustBar />
      <ServiceGrid />
      <WhyUs />
      <HowItWorks />
      <QuizTeaser />
      <Faqs />
      <Consultation />
    </>
  );
}
